import { Sticker, Music2, Volume2, Bell, Cpu, Sparkles, Shield, Palette, Paintbrush, Phone, Coins, Users, Clock, Mail, Calendar, Handshake, NotebookPen, MonitorDot, Bot, UserPlus } from 'lucide-react'
import type { ComponentType } from 'react'
import type { WebModule } from '@os/contracts/web'
import { DelegatedTab } from '../../components/admin/DelegatedTab'

// lucide-react exports ForwardRefExoticComponent which doesn't directly satisfy
// ComponentType<{size?:number}> under strict assignment. This cast is safe —
// every lucide icon accepts a `size` prop.
type Icon = ComponentType<{ size?: number }>

// In-core "platform" admin provider: the OS-level global-admin sections
// that are not owned by any sibling module. Tabs + chrome mirror the old
// hardcoded ADMIN_TABS/render in pages/AdminPanel.tsx exactly. Panels are
// lazy so the global-admin bundle is unchanged.
const platformAdminModule: WebModule = {
  id: 'platform',
  version: '0.1.0',
  adminSections: [
    {
      id: 'stickers',
      label: 'Stickers',
      subtitle: 'Global sticker packs',
      scope: 'platform',
      order: 50,
      icon: Sticker as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: StickerPacksAdmin } = await import('../../components/admin/StickerPacksAdmin')
        return {
          default: () => (
            <DelegatedTab icon={<Sticker size={14} />} title="Stickers" subtitle="Sticker packs visible to every user">
              <StickerPacksAdmin />
            </DelegatedTab>
          ),
        }
      },
    },
    {
      id: 'sounds',
      label: 'Soundboard',
      subtitle: 'Voice-room soundboard',
      scope: 'platform',
      order: 60,
      icon: Music2 as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: SoundboardAdmin } = await import('@os/spaces/web/components/admin/SoundboardAdmin')
        return {
          default: () => (
            <DelegatedTab icon={<Music2 size={14} />} title="Soundboard" subtitle="Voice-room sound effects everyone can play">
              <SoundboardAdmin />
            </DelegatedTab>
          ),
        }
      },
    },
    {
      id: 'systemSounds',
      label: 'System sounds',
      subtitle: 'Replace built-in join/leave/message/call sounds - global',
      scope: 'platform',
      order: 70,
      icon: Volume2 as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: SystemSoundsAdmin } = await import('../../components/admin/SystemSoundsAdmin')
        return {
          default: () => (
            <DelegatedTab icon={<Volume2 size={14} />} title="System sounds" subtitle="Replace the built-in join/leave/message/call sounds - global for everyone">
              <SystemSoundsAdmin />
            </DelegatedTab>
          ),
        }
      },
    },
    {
      id: 'notifications',
      label: 'Notifications',
      subtitle: 'Edit notification templates',
      scope: 'platform',
      order: 80,
      icon: Bell as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: NotificationTemplatesAdmin } = await import('../../components/admin/NotificationTemplatesAdmin')
        return {
          default: () => (
            <DelegatedTab icon={<Bell size={14} />} title="Notification templates" subtitle="Edit per-channel email/SMS/push/in-app templates served by the notify() platform">
              <NotificationTemplatesAdmin />
            </DelegatedTab>
          ),
        }
      },
    },
    {
      id: 'jobs',
      label: 'Jobs',
      subtitle: 'Cron + queue health',
      scope: 'platform',
      order: 90,
      icon: Cpu as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: JobsAdmin } = await import('../../components/admin/JobsAdmin')
        return {
          default: () => (
            <DelegatedTab icon={<Cpu size={14} />} title="Jobs" subtitle="Cron + queue health across all registered modules">
              <JobsAdmin />
            </DelegatedTab>
          ),
        }
      },
    },
    {
      id: 'llm',
      label: 'AI prompts',
      subtitle: 'AI prompt registry + telemetry',
      scope: 'platform',
      order: 100,
      icon: Sparkles as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: LlmPromptsAdmin } = await import('../../components/admin/LlmPromptsAdmin')
        return {
          default: () => (
            <DelegatedTab icon={<Sparkles size={14} />} title="AI prompts" subtitle="Versioned LLM prompts and recent call telemetry">
              <LlmPromptsAdmin />
            </DelegatedTab>
          ),
        }
      },
    },
    {
      id: 'activity',
      label: 'Activity',
      subtitle: 'Audit log - who did what, when, device/IP',
      scope: 'platform',
      order: 110,
      icon: Shield as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: ActivityLogTab } = await import('../../components/admin/ActivityLogTab')
        return {
          default: () => (
            <DelegatedTab icon={<Shield size={14} />} title="Activity" subtitle="Audit log - who did what, when, from which device/IP (metadata only, no message content)">
              <ActivityLogTab />
            </DelegatedTab>
          ),
        }
      },
    },
    {
      id: 'design',
      label: 'Design',
      subtitle: 'Canonical layouts, tables and sections - the design kit',
      scope: 'platform',
      order: 120,
      icon: Palette as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: DesignSection } = await import('../../components/admin/DesignSection')
        return { default: () => <div className="min-h-0 flex-1 overflow-y-auto"><DesignSection /></div> }
      },
    },
    {
      id: 'telephony',
      label: 'Telephony',
      subtitle: 'Phone numbers, SMS compliance, purchases',
      scope: 'platform',
      order: 130,
      icon: Phone as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: TelephonyAdminSection } = await import('../../components/admin/telephony/TelephonyAdminSection')
        return { default: () => <div className="min-h-0 flex-1 overflow-y-auto"><TelephonyAdminSection /></div> }
      },
    },
    {
      id: 'signups',
      label: 'Signups',
      subtitle: 'ViewSX self-service + managed launch intents',
      scope: 'platform',
      order: 135,
      icon: UserPlus as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: SignupsSection } = await import('../../components/admin/SignupsSection')
        return { default: () => <div className="min-h-0 flex-1 overflow-y-auto"><SignupsSection /></div> }
      },
    },
    {
      id: 'brands',
      label: 'White label',
      subtitle: 'Per-tenant brands — name, domain, accent and logos',
      scope: 'platform',
      order: 137,
      icon: Paintbrush as Icon,
      // Global admin ONLY — white label is a sold product, so a tenant must
      // never be able to switch its own branding on. The server route carries
      // the real guard (authenticate + requireAdmin); this gate only hides the
      // tab. Spec: ai/os (umbrella)
      // docs/superpowers/specs/2026-07-27-viewsx-white-label-design.md §4–§5.
      gate: { globalAdmin: true },
      component: async () => {
        const { default: BrandsSection } = await import('../../pages/admin/BrandsSection')
        return { default: () => <div className="min-h-0 flex-1 overflow-y-auto"><BrandsSection /></div> }
      },
    },
    {
      id: 'spending',
      label: 'Spend & usage',
      subtitle: 'Cost + resource usage across all workspaces',
      scope: 'platform',
      order: 140,
      icon: Coins as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: SpendUsagePanel } = await import('../../components/admin/monitoring/SpendUsagePanel')
        return {
          default: () => (
            <DelegatedTab icon={<Coins size={14} />} title="Spend & usage" subtitle="Money and resources per workspace">
              <SpendUsagePanel />
            </DelegatedTab>
          ),
        }
      },
    },
    {
      id: 'module:people',
      label: 'People',
      subtitle: 'Onboarding console - status, bulk invites',
      scope: 'platform',
      order: 5,
      moduleKey: 'people',
      icon: Users as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: PeopleAdminSection } = await import('./peopleAdminSection')
        return { default: () => <PeopleAdminSection /> }
      },
    },
    {
      id: 'module:tracker',
      label: 'Tracker',
      subtitle: 'Activity-tracking policy (workspace defaults)',
      scope: 'platform',
      order: 0,
      moduleKey: 'tracker',
      icon: Clock as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: TrackerPolicyAdmin } = await import('@os/tracker/web/components/admin/TrackerPolicyAdmin')
        return { default: () => <div className="min-h-0 flex-1 overflow-y-auto"><TrackerPolicyAdmin /></div> }
      },
    },
    {
      id: 'module:mail',
      label: 'Mail',
      subtitle: 'Connected accounts & sync health',
      scope: 'platform',
      order: 3,
      moduleKey: 'mail',
      icon: Mail as Icon,
      gate: { globalAdmin: true },
      // MailAdminPanel self-applies `min-h-0 flex-1 overflow-y-auto` — no wrapper div.
      component: async () => {
        const { default: MailAdminPanel } = await import('@os/mail/web/components/admin/MailAdminPanel')
        return { default: () => <MailAdminPanel /> }
      },
    },
    {
      id: 'module:calendar',
      label: 'Calendar',
      subtitle: 'Connected calendars & sync health',
      scope: 'platform',
      order: 4,
      moduleKey: 'calendar',
      icon: Calendar as Icon,
      // CalendarAdminPanel self-applies `min-h-0 flex-1 overflow-y-auto` — no wrapper div.
      gate: { globalAdmin: true },
      component: async () => {
        const { default: CalendarAdminPanel } = await import('@os/calendar/web/components/admin/CalendarAdminPanel')
        return { default: () => <CalendarAdminPanel /> }
      },
    },
    {
      id: 'module:crm',
      label: 'CRM',
      subtitle: 'Engine stats - entities & extraction freshness',
      scope: 'platform',
      order: 5,
      moduleKey: 'crm',
      icon: Handshake as Icon,
      // CrmAdminPanel self-applies `min-h-0 flex-1 overflow-y-auto` — no wrapper div.
      gate: { globalAdmin: true },
      component: async () => {
        const { default: CrmAdminPanel } = await import('@os/crm/web/components/admin/CrmAdminPanel')
        return { default: () => <CrmAdminPanel /> }
      },
    },
    {
      id: 'module:notes',
      label: 'Notes',
      subtitle: 'Notes stats - documents, sharing, owners',
      scope: 'platform',
      order: 6,
      moduleKey: 'notes',
      icon: NotebookPen as Icon,
      // NotesAdminPanel self-applies `min-h-0 flex-1 overflow-y-auto` — no wrapper div.
      gate: { globalAdmin: true },
      component: async () => {
        const { default: NotesAdminPanel } = await import('@os/notes/web/components/admin/NotesAdminPanel')
        return { default: () => <NotesAdminPanel /> }
      },
    },
    {
      id: 'module:probe',
      label: 'Probe',
      subtitle: 'Remote-support agents - devices, sessions, policies',
      scope: 'platform',
      order: 7,
      moduleKey: 'probe',
      icon: MonitorDot as Icon,
      // ProbeAdminPanel self-applies `min-h-0 flex-1 overflow-y-auto` — no wrapper div.
      gate: { globalAdmin: true },
      component: async () => {
        const { default: ProbeAdminPanel } = await import('@os/probe/web/components/admin/ProbeAdminPanel')
        return { default: () => <ProbeAdminPanel /> }
      },
    },
    {
      id: 'module:assistant',
      label: 'AI Assistant',
      subtitle: 'Conversation usage - counts, models, top users',
      scope: 'platform',
      order: 8,
      moduleKey: 'assistant',
      icon: Bot as Icon,
      // AssistantAdminPanel self-applies `min-h-0 flex-1 overflow-y-auto` — no wrapper div.
      gate: { globalAdmin: true },
      component: async () => {
        const { default: AssistantAdminPanel } = await import('@os/assistant/web/components/admin/AssistantAdminPanel')
        return { default: () => <AssistantAdminPanel /> }
      },
    },
  ],
}

export default platformAdminModule
